import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { titleCase } from '@/lib/utils/format';

/**
 * Contract pulse surface (T-0069 / E06-F07).
 *
 * Five endpoints:
 *   - POST   /api/v1/engagements/:engagement_id/contract/pulse/draft
 *   - PATCH  /api/v1/engagements/:engagement_id/contract/pulse/:pulse_id
 *   - POST   /api/v1/engagements/:engagement_id/contract/pulse/:pulse_id/publish
 *   - GET    /api/v1/engagements/:engagement_id/contract/pulse/:pulse_id
 *   - GET    /api/v1/engagements/:engagement_id/contract/pulse?status=...
 *
 * The pulse screen uses all five — the list lights up the cycle history,
 * the latest draft is the inline editable narrative, draft triggers a
 * fresh AI cycle, patch saves the consultant's edits, publish freezes
 * the snapshot + requests a PDF render via T-0059.
 *
 * Citations on the wire are intentionally permissive (`unknown[]`) on
 * write because the backend accepts any well-formed array; on read we
 * coerce into the strongly-typed shape that the citation chip primitive
 * (T-0093) consumes.
 */

export const PULSE_POSTURES = ['red', 'yellow', 'green'] as const;
export type PulsePosture = (typeof PULSE_POSTURES)[number];

export const PULSE_STATUSES = ['draft', 'publishing', 'published'] as const;
export type PulseStatus = (typeof PULSE_STATUSES)[number];

export const PulseCitationSchema = z.object({
  ref_kind: z.string(),
  ref_id: z.string(),
  excerpt: z.string().optional(),
  /** The cited record's name, attached by the API at draft time (C2-31). */
  label: z.string().optional(),
});
export type PulseCitation = z.infer<typeof PulseCitationSchema>;

export const PulseEvidenceSchema = z.object({
  facts: z.number(),
  deliverables: z.number(),
  checkpoints: z.number(),
  deviations: z.number(),
  total: z.number(),
  sufficient: z.boolean(),
});
export type PulseEvidence = z.infer<typeof PulseEvidenceSchema>;

export const PulseSchema = z.object({
  pulse_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  cycle_label: z.string(),
  status: z.enum(['draft', 'publishing', 'published']),
  overall_posture: z.enum(['red', 'yellow', 'green']).nullable(),
  posture_override_id: z.string().uuid().nullable(),
  narrative_text: z.string().nullable(),
  narrative_citations: z.array(PulseCitationSchema),
  narrative_sections: z.record(z.unknown()),
  is_safe_refusal: z.boolean(),
  narrative_outcome: z.string().nullable(),
  prompt_version: z.string().nullable(),
  model_id: z.string().nullable(),
  token_cost_cents: z.number(),
  snapshot_id: z.string().uuid().nullable(),
  pdf_report_id: z.string().uuid().nullable(),
  published_at: z.string().nullable(),
  published_by_user_id: z.string().uuid().nullable(),
  drafted_by_user_id: z.string().uuid(),
  created_at: z.string(),
  updated_at: z.string(),
  /**
   * What the evidence gate counted at draft time (C2-11); null for pulses
   * drafted before the gate, absent from an older server.
   */
  evidence: PulseEvidenceSchema.nullable().optional(),
});
export type Pulse = z.infer<typeof PulseSchema>;

/**
 * Whether a pulse has a narrative to edit or publish (C2-12). A safe
 * refusal or an empty draft has nothing to stand behind: the API refuses
 * to publish it (`pulse.refusal_must_edit` / `pulse.empty_narrative`), so
 * the screen keeps Publish and Edit disabled instead of offering a click
 * that can only fail. A fresh draft, once records are approved, is the way on.
 */
export function pulseHasNarrative(p: Pick<Pulse, 'is_safe_refusal' | 'narrative_text'>): boolean {
  return !p.is_safe_refusal && (p.narrative_text ?? '').trim().length > 0;
}

/**
 * The evidence the gate counted, in words: "12 contract facts · 1
 * deliverable · 1 checkpoint · 1 deviation". Sources with nothing are left
 * out; all four empty reads "no approved records". Counts are the
 * service's, never the model's.
 */
export function pulseEvidenceSummary(e: PulseEvidence): string {
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => {
    if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  add(e.facts, 'contract fact', 'contract facts');
  add(e.deliverables, 'deliverable', 'deliverables');
  add(e.checkpoints, 'checkpoint', 'checkpoints');
  add(e.deviations, 'deviation', 'deviations');
  return parts.length > 0 ? parts.join(' · ') : 'no approved records';
}

export const PulseListSchema = z.object({
  items: z.array(PulseSchema),
  next_cursor: z.string().nullable(),
});
export type PulseList = z.infer<typeof PulseListSchema>;

export interface ListPulsesOpts {
  status?: PulseStatus;
  cycle_label?: string;
  cursor?: string;
  limit?: number;
}

export function listPulses(engagementId: string, opts: ListPulsesOpts = {}): Promise<PulseList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/contract/pulse`,
    PulseListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function usePulseListQuery(engagementId: string | undefined, opts: ListPulsesOpts = {}) {
  return useQuery({
    queryKey: ['pulses', engagementId, opts] as const,
    queryFn: () => listPulses(engagementId!, opts),
    enabled: Boolean(engagementId),
  });
}

export function getPulse(engagementId: string, pulseId: string): Promise<Pulse> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/contract/pulse/${encodeURIComponent(
      pulseId,
    )}`,
    PulseSchema,
  );
}

export function usePulseDetailQuery(engagementId: string | undefined, pulseId: string | undefined) {
  return useQuery({
    queryKey: ['pulse', engagementId, pulseId] as const,
    queryFn: () => getPulse(engagementId!, pulseId!),
    enabled: Boolean(engagementId && pulseId),
  });
}

// ===========================================================================
// Draft / Edit / Publish mutations
// ===========================================================================

export interface DraftPulseInput {
  cycle_label: string;
  audience?: string;
  summary_focus?: string;
}

export const DraftPulseResultSchema = z.object({
  pulse_id: z.string().uuid(),
  status: z.literal('draft'),
  cycle_label: z.string(),
  overall_posture: z.enum(['red', 'yellow', 'green']),
  narrative_text: z.string(),
  narrative_sections: z.record(z.unknown()),
  citations: z.array(PulseCitationSchema),
  is_safe_refusal: z.boolean(),
  narrative_outcome: z.string(),
  audience: z.string(),
});
export type DraftPulseResult = z.infer<typeof DraftPulseResultSchema>;

export function draftPulse(
  engagementId: string,
  input: DraftPulseInput,
  idempotencyKey?: string,
): Promise<DraftPulseResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/contract/pulse/draft`,
    DraftPulseResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useDraftPulseMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input, idempotencyKey }: { input: DraftPulseInput; idempotencyKey?: string }) =>
      draftPulse(engagementId!, input, idempotencyKey),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['pulses', engagementId] });
      }
    },
  });
}

export interface PulseOverrideInput {
  tag: string;
  rationale: string;
}

export interface EditPulseInput {
  narrative_text?: string;
  narrative_citations?: unknown[];
  narrative_sections?: Record<string, unknown>;
  overall_posture?: PulsePosture;
  cycle_label?: string;
  override?: PulseOverrideInput;
}

export const EditPulseResultSchema = z.object({
  pulse_id: z.string().uuid(),
  status: z.enum(['draft', 'publishing', 'published']),
  cycle_label: z.string(),
  overall_posture: z.enum(['red', 'yellow', 'green']).nullable(),
});
export type EditPulseResult = z.infer<typeof EditPulseResultSchema>;

export function editPulse(
  engagementId: string,
  pulseId: string,
  input: EditPulseInput,
  idempotencyKey?: string,
): Promise<EditPulseResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/contract/pulse/${encodeURIComponent(
      pulseId,
    )}`,
    EditPulseResultSchema,
    {
      method: 'PATCH',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useEditPulseMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pulseId,
      input,
      idempotencyKey,
    }: {
      pulseId: string;
      input: EditPulseInput;
      idempotencyKey?: string;
    }) => editPulse(engagementId!, pulseId, input, idempotencyKey),
    onSuccess: (_data, vars) => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['pulses', engagementId] });
        void qc.invalidateQueries({ queryKey: ['pulse', engagementId, vars.pulseId] });
      }
    },
  });
}

export const PublishPulseResultSchema = z.object({
  pulse_id: z.string().uuid(),
  status: z.literal('published'),
  snapshot_id: z.string().uuid(),
  pdf_report_id: z.string().uuid(),
  cycle_label: z.string(),
  overall_posture: z.enum(['red', 'yellow', 'green']).nullable(),
  published_at: z.string(),
});
export type PublishPulseResult = z.infer<typeof PublishPulseResultSchema>;

export function publishPulse(
  engagementId: string,
  pulseId: string,
  idempotencyKey?: string,
): Promise<PublishPulseResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/contract/pulse/${encodeURIComponent(
      pulseId,
    )}/publish`,
    PublishPulseResultSchema,
    {
      method: 'POST',
      body: {},
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function usePublishPulseMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pulseId, idempotencyKey }: { pulseId: string; idempotencyKey?: string }) =>
      publishPulse(engagementId!, pulseId, idempotencyKey),
    onSuccess: (_data, vars) => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['pulses', engagementId] });
        void qc.invalidateQueries({ queryKey: ['pulse', engagementId, vars.pulseId] });
      }
    },
  });
}

// ===========================================================================
// PulseCitation → CitationChipData translation seam
// ===========================================================================

/**
 * Translate a backend `PulseCitation` into the shape the citation chip
 * primitive consumes. The chip needs an `id`, `doc_label`, optional
 * locator (page/section/cell), and a confidence (defaulted to 0.85 when
 * not provided by the LLM cite payload — the chip's high/medium/low
 * band must always render).
 *
 * For pulse citations the backend returns the `ref_kind` (deliverable /
 * deviation / checkpoint / contract fact) + `ref_id`, and since C2-31 the
 * cited record's `label`. The chip shows the label, else the model's
 * excerpt, else the kind's name; the id stays in the chip's key and is
 * never printed.
 */
export function pulseCitationToChip(
  c: PulseCitation,
  idx: number,
): {
  id: string;
  doc_label: string;
  page?: number | null;
  section?: string | null;
  confidence: number;
} {
  return {
    id: `${c.ref_kind}:${c.ref_id}:${idx}`,
    doc_label: pulseCiteLabel(c),
    confidence: 0.85,
  };
}

/** What a pulse citation's ref_kind is called when the record has no label. */
const PULSE_REF_KIND_LABELS: Record<string, string> = {
  edl_item: 'Deliverable',
  deliverable: 'Deliverable',
  deviation: 'Deviation',
  checkpoint: 'Checkpoint',
  checkpoint_assessment: 'Checkpoint assessment',
  checkpoint_score: 'Checkpoint score',
  contract_fact: 'Contract fact',
  sow: 'Contract baseline',
  sow_item: 'Contract baseline',
  document: 'Document',
};

export function pulseCiteLabel(c: PulseCitation): string {
  const label = c.label?.trim();
  if (label) return label.length > 60 ? `${label.slice(0, 57)}…` : label;
  if (c.excerpt && c.excerpt.trim().length > 0) {
    const excerpt = c.excerpt.trim();
    return excerpt.length > 60 ? `${excerpt.slice(0, 57)}…` : excerpt;
  }
  return PULSE_REF_KIND_LABELS[c.ref_kind] ?? titleCase(c.ref_kind);
}
